import { DEFAULT_CURRENCY, type User } from '@closetly/core';
import { useMemo, useState } from 'react';
import { useBoards, useItems, useMe, useOutfits } from '../lib/queries';
import { navigate } from '../lib/router';
import { OutfitPin } from './OutfitPin';

const ALL = 'Alle';

/** Every outfit as a pin, filterable by the boards they are on. */
export function OutfitsPage({ user }: { user: User }) {
  const outfits = useOutfits(user.id);
  const boards = useBoards(user.id);
  const items = useItems(user.id);
  const me = useMe(user);
  const [filter, setFilter] = useState<string>(ALL);
  const currency = me.data?.currency || DEFAULT_CURRENCY;

  const all = useMemo(() => outfits.data ?? [], [outfits.data]);
  // boards with outfits on them become chips
  const filters = useMemo(() => (boards.data ?? []).filter((b) => b.outfits.length > 0), [boards.data]);
  const activeBoard = filters.find((b) => b.id === filter);
  const shown = activeBoard ? all.filter((o) => activeBoard.outfits.includes(o.id)) : all;

  return (
    <>
      {filters.length > 0 && (
        <div className="chips board-chips" role="group" aria-label="Filter">
          <button type="button" className="chip" aria-pressed={!activeBoard} onClick={() => setFilter(ALL)}>
            {ALL}
          </button>
          {filters.map((b) => (
            <button
              key={b.id}
              type="button"
              className="chip"
              aria-pressed={b.id === activeBoard?.id}
              onClick={() => setFilter(b.id)}
            >
              {b.name}
            </button>
          ))}
        </div>
      )}

      {outfits.isPending ? (
        <div className="loading">Lade deine Outfits…</div>
      ) : all.length === 0 ? (
        <div className="empty">
          <strong>Noch keine Outfits</strong>
          Stell dir zusammen, was du haben willst: Teile aus deiner Wunschliste oder direkt per Link aus dem Shop.
          Closetly zeigt dir, was noch fehlt und was es kostet.
        </div>
      ) : (
        <div className="board">
          {shown.map((o) => (
            <OutfitPin key={o.id} outfit={o} items={items.data ?? []} currency={currency} />
          ))}
        </div>
      )}

      <button className="fab" type="button" onClick={() => navigate('/outfits/neu')}>
        + Outfit
      </button>
    </>
  );
}
